import { useTheme } from '@mui/material';
import { debounce } from 'essential-utils';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { FlexBox, FlexBoxProps } from '../box';
import { styled } from '../styled';
import { usePaletteColor } from '../theme';
import { Tooltip, TooltipProps } from '../tooltip';
import { Chip, ChipProps } from '.';

const MIN_CHIP_WIDTH = 80;

const FadeOut = styled('div')(({ theme }) => ({
    position: 'absolute',
    width: theme.spacing(4),
    height: '100%',
    pointerEvents: 'none',
    maskImage: `linear-gradient(to left, white ${theme.spacing(1)}, rgba(255, 255, 255, 0) 100%)`
}));

const BackBox = styled('div')({
    position: 'absolute',
    right: 0,
    height: '100%',
    backgroundColor: 'white',
    zIndex: 1
});

export interface ChipGroupProps
    extends TypedOmit<
        FlexBoxProps,
        | 'gap'
        | 'rowGap'
        | 'columnGap'
        | 'padding'
        | 'p'
        | 'px'
        | 'py'
        | 'autoShrink'
    > {
    children: React.ReactElement<ChipProps>[];
    placement?: TooltipProps['placement'];
    onVisibilityChange?: TooltipProps['onVisibilityChange'];
}

export const ChipGroup: React.FC<ChipGroupProps> = ({
    children = [],
    placement = 'auto',
    onVisibilityChange,
    ...flexBoxProps
}) => {
    const { spacing } = useTheme();
    const [containerWidth, setContainerWidth] = useState<number>(0);
    const [node, setNode] = useState<HTMLDivElement | undefined>();
    const [plusChipWidth, setPlusChipWidth] = useState(0);
    const backgroundColor = usePaletteColor(
        flexBoxProps.bgcolor ?? 'background.paper'
    );
    const chipRefs = useRef<(HTMLDivElement | null)[]>([]);

    const GAP_UNIT = 1;
    const GAP = parseInt(spacing(GAP_UNIT), 10);

    const [hiddenChips, setHiddenChips] = useState<React.ReactElement[]>([]);

    const calculate = useCallback(() => {
        if (!node) {
            return;
        }

        const containerWidth = node.offsetWidth || 0;
        setContainerWidth(containerWidth);
        const widths = chipRefs.current.map((ref) => ref?.offsetWidth || 0);

        const { hidden } = widths.reduce(
            (acc, width, index) => {
                if (acc.collectedWidth + width <= containerWidth) {
                    acc.collectedWidth += width + GAP;
                    return acc;
                }
                if (acc.collectedWidth + MIN_CHIP_WIDTH < containerWidth) {
                    acc.collectedWidth += MIN_CHIP_WIDTH;
                }
                acc.hidden.push(children[index]);
                return acc;
            },
            {
                hidden: [] as React.ReactElement[],
                collectedWidth: 0
            }
        );
        setHiddenChips(hidden);
    }, [children, node, GAP]);

    useEffect(() => {
        if (!node) {
            return;
        }
        const debouncedCalculate = debounce(calculate, 25);

        const observer = new ResizeObserver(() => {
            debouncedCalculate();
        });

        observer.observe(node);

        return () => {
            debouncedCalculate.cancel();
            observer?.disconnect();
            setNode(undefined);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [node]);

    const refCallback = React.useCallback((node: HTMLDivElement | null) => {
        if (!node) {
            return;
        }
        setNode(node);
    }, []);

    useEffect(() => {
        calculate();
    }, [calculate, children]);

    const refPlusChipCallback = React.useCallback(
        (node: HTMLDivElement | null) => {
            if (!node) {
                return;
            }
            setPlusChipWidth(node.offsetWidth);
        },
        []
    );

    const Chips = () => {
        if (!children.length) {
            return null;
        }
        if (children.length === 1) {
            const chip = React.cloneElement(children[0], {
                maxWidth: containerWidth - 2 * GAP
            });
            return (
                <FlexBox alignItems="center" height="100%">
                    {chip}
                </FlexBox>
            );
        }
        return (
            <FlexBox
                overflow="hidden"
                gap={GAP_UNIT}
                alignItems="center"
                height="100%"
                maxWidth={containerWidth}
            >
                <FlexBox display="flex" gap={GAP_UNIT} alignItems="center">
                    {children.map((child, index) => (
                        <FlexBox
                            ref={(el: HTMLDivElement | null) =>
                                (chipRefs.current[index] = el)
                            }
                            key={`chip-${index}`}
                            alignItems="center"
                            justifyContent="center"
                        >
                            {child}
                        </FlexBox>
                    ))}
                </FlexBox>
                {Boolean(hiddenChips.length) && (
                    <FlexBox position="absolute" right={0}>
                        {Boolean(plusChipWidth) && (
                            <>
                                <FadeOut
                                    data-role="chip-group-fade-out"
                                    sx={{
                                        backgroundColor,
                                        right: `${plusChipWidth - 1}px`
                                    }}
                                />
                                <BackBox
                                    sx={{
                                        width: `${plusChipWidth}px`
                                    }}
                                />
                            </>
                        )}
                        <Tooltip
                            onVisibilityChange={onVisibilityChange}
                            placement={placement}
                            title={
                                <FlexBox gap={GAP_UNIT} flexWrap="wrap" p={1}>
                                    {hiddenChips.map((child, index) => (
                                        <FlexBox
                                            key={`hidden-${index}`}
                                            alignItems="center"
                                            justifyContent="center"
                                        >
                                            {child}
                                        </FlexBox>
                                    ))}
                                </FlexBox>
                            }
                            mode="light"
                        >
                            <Chip
                                ref={refPlusChipCallback}
                                label={`+${hiddenChips.length}`}
                                sx={{
                                    zIndex: 2
                                }}
                            ></Chip>
                        </Tooltip>
                    </FlexBox>
                )}
            </FlexBox>
        );
    };

    return (
        <FlexBox
            data-role="chip-group"
            position="relative"
            width="100%"
            alignItems="center"
            height="100%"
            {...flexBoxProps}
            ref={refCallback}
        >
            {Boolean(containerWidth) && <Chips />}
        </FlexBox>
    );
};
